import { chunkArray, genUUID } from '../../index';
import humps from 'humps';

// 使用和logger中一致的deviceId
const DEVICE_ID_KEY = 'logdeviceid';

const DEFAULT_OPTIONS = {
    appVersion: '0.0.1'
};

const DELAY_TIME_MS = 10 * 1000;

const asynLocalStorage = () => {
    return window.localStorage;
};

class EventTracker {
    static UPLOAD_TRACKS_URL = '/stat/api/v2/track/collection';

    /**
   * @param systemName should register system in https://silence.nioint.com
   * @param [options]
   *  @param options.appVersion your app version
   *  @param {boolean} [options.verbose] show more debug logs
   *  @param {string} [options.env] if use porter, should provide current env.
   *  @param {boolean} [options.usePorter] use porter to proxy tracks collect.
   *  @param {string} [options.appId] if use porter, should provide current appId.
   *  @param {number|string} [options.userId] set userId, then put to every collection data.
   */
    constructor(systemName, options = {}) {
        this.trackQueue = [];
        this.trackTimeoutId = null;
        this.delayTimeMs = DELAY_TIME_MS;
        this.options = {
            ...DEFAULT_OPTIONS,
            ...options
        };
        this.systemName = systemName;
        if (this.options.usePorter && !this.options.env) {
            throw new Error(
                'if usePorter, should provide your current env. canbe dev/test/stg/prod'
            );
        }
    }
    _verbose() {
        if (this.options.verbose) {
            console.debug(...arguments);
        }
    }
    _getDeviceId() {
        if (!this.deviceId) {
            let deviceId;
            deviceId = asynLocalStorage().getItem(DEVICE_ID_KEY);
            if (!deviceId) {
                deviceId = genUUID();
                asynLocalStorage().setItem(DEVICE_ID_KEY, deviceId);
            }
            this.deviceId = deviceId;
        }
        return this.deviceId;
    }

    getPorterAPIUrl() {
        const PORTER_API_PATH = '/api/v1/tracks/collect';

        if (this.options.porterAPIUrl) {
            return this.options.porterAPIUrl;
        }
        const env = this.options.env === 'prod' ? '' : `-${this.options.env}`;
        return `https://porter${env}.nio.com${PORTER_API_PATH}`;
    }

    /**
   * 数据埋点
   * @param data
   *  @param {String} data.key
   *  @param {Object} [data.custom]
   * @returns {Promise<void>}
   */
    collect(data) {
        let promise = Promise.resolve();
        const eventTime = Math.floor(new Date().getTime() / 1000);
        const userId = this.options.userId;
        this._verbose('start collect track data');
        if (this.trackQueue.length >= 50) {
            this._verbose(
                'more then 50 tracks, start upload Collected content'
            );
            promise = this.uploadCollected();
        }
        return promise.then(() => {
            this._verbose('add data to queue', data);
            this.trackQueue.unshift({
                eventTime,
                userId,
                ...data
            });
            if (!this.trackTimeoutId) {
                /*first time, start ticktock*/
                this._verbose(
                    'no track timeout exist, trigger next upload timeout'
                );
                this._triggerNextUploadTimeout();
            }
        });
    }
    /**
     * 数据埋点: 神策+insight
     * @param data
     *  @param {String} data.key
     *  @param {String} [data.eventType]
     *  @param {Object} [data.custom]
     * @returns {Promise<void>}
     */
    collectPlus(data) {
        this.trackSaEvent(data);
    }
    _triggerNextUploadTimeout() {
        if (this.trackTimeoutId) {
            //eslint-disable-next-line
            clearTimeout(this.trackTimeoutId);
        }
        this._verbose('start timer upload at', new Date().getTime());
        //eslint-disable-next-line
        this.trackTimeoutId = setTimeout(() => {
            this.uploadCollected();
            this.trackTimeoutId = null;
        }, this.delayTimeMs);
    }

    uploadCollected() {
        const { trackQueue } = this;
        if (!trackQueue.length) {
            this._triggerNextUploadTimeout();
            return Promise.resolve();
        }
        const queueLen = trackQueue.length;
        const queryString = {
            app_ver: encodeURIComponent(this.options.appVersion || '1.0.0'),
            system_name: this.systemName
        };

        return Promise.all(
            chunkArray(trackQueue, 50).map(i => {
                const data = {
                    events: i,
                    device_id: this._getDeviceId()
                };
                let url;
                const options = {
                    method: 'POST',
                    body: JSON.stringify(humps.decamelizeKeys(data)),
                    //eslint-disable-next-line
                    headers: new Headers({
                        'Content-Type': 'application/json'
                    })
                };
                if (this.options.usePorter) {
                    options.mode = 'cors';
                    options.headers.append(
                        'Referer',
                        `${window.location.protocol}//${window.location.host}`
                    );
                    url = `${this.getPorterAPIUrl()}?app_id=${this.options
                        .appId}&app_ver=${queryString.app_ver}&system_name=${queryString.system_name}`;
                } else {
                    url = `${EventTracker.UPLOAD_TRACKS_URL}?app_ver=${queryString.app_ver}&system_name=${queryString.system_name}`;
                }

                //eslint-disable-next-line
                return fetch(url, options)
                    .then(res => res.json())
                    .then(data => {
                        if (data.result_code === 'success') {
                            this._verbose('success');
                        } else {
                            this._verbose('error,wait to try upload next time');
                            const error = new Error(
                                data.message || 'unknown error'
                            );
                            error.code = data.result_code;
                            error.result_code = data.result_code;
                            throw error;
                        }
                    });
            })
        )
            .then(() => {
                this._verbose(`collect track event success`, trackQueue);
                this.trackQueue = this.trackQueue.slice(
                    0,
                    this.trackQueue.length - queueLen
                );
                this._triggerNextUploadTimeout();
            })
            .catch(e => {
                this._verbose(
                    'error:',
                    `collect track event error: ${e.message}`
                );
                this._triggerNextUploadTimeout();
            });
    }
    flushAll() {
        this._verbose(`flush queue items`);
        if (this.trackTimeoutId) {
            //eslint-disable-next-line
            clearTimeout(this.trackTimeoutId);
            this.trackTimeoutId = null;
        }
        this.uploadCollected();
    }
    trackSaEvent(data) {
        try {
            // eslint-disable-next-line prefer-const
            let { key, custom } = data;
            console.log('start track sa event', key);
            if (!custom) {
                custom = {};
            }
            window.sensors.track(key, {
                platform_type: 'h5',
                ...custom
            });
            console.log('end track sa event', key);
        } catch (e) {
            console.error('track sa event error', e);
        }
    }
}

export { EventTracker };



// WEBPACK FOOTER //
// ./src/util/report/tracker/tracker.js